import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { cn } from '@/utils/сn';
import ChevronDownIcon from '/public/icons/chevron-down.svg';
import { useElementSize } from '@/hooks/useElementSize';

type AccordionVariant = 'default' | 'white';
type ArrowPosition = 'right' | 'left';

type AccordionProps = {
  header: string;
  description?: string;
  children: ReactNode;
  defaultOpen?: boolean;
  variant?: AccordionVariant;
  arrowPosition?: ArrowPosition;
  className?: string;
  sectionClassName?: string;
  isHiddenBorder?: boolean;
  headerClassName?: string;
  as?: HeadingTag;
};
type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export const SectionAccordion = ({
  header,
  as = 'h4',
  description,
  children,
  defaultOpen = false,
  variant = 'default',
  arrowPosition = 'right',
  className,
  sectionClassName,
  isHiddenBorder,
  headerClassName,
}: AccordionProps) => {
  const Heading = as;
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);
  const { height } = useElementSize<HTMLDivElement>(ref);

  const arrowLeft = arrowPosition === 'left';
  const isWhite = variant === 'white';

  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden border',
        isWhite ? 'bg-white border-transparent' : 'bg-blue-50 border-transparent'
      )}
    >
      <button
        type="button"
        aria-expanded={!!open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center px-7.5 py-4 focus:outline-none cursor-pointer',
          arrowLeft ? 'gap-4 justify-start' : 'justify-between',
          className
        )}
      >
        {arrowLeft && (
          <ChevronDownIcon
            className={cn(
              'w-3 h-3 fill-current text-blue-900 !stroke-[1.2px] !stroke-current transition-transform',
              open ? 'rotate-180' : 'rotate-0'
            )}
          />
        )}

        <div className="flex-1 text-left">
          <Heading className={cn('text-black !font-inter !font-medium', headerClassName)}>{header}</Heading>

          {description && <p className="text-body-regular text-grey-700">{description}</p>}
        </div>

        {!arrowLeft && (
          <ChevronDownIcon
            className={cn(
              'w-3 h-3 fill-current text-blue-900 !stroke-[1.2px] !stroke-current transition-transform',
              open ? 'rotate-180' : 'rotate-0'
            )}
          />
        )}
      </button>

      <div
        ref={ref}
        className={cn(
          'overflow-hidden transition-[max-height] duration-300 ease-in-out',
          open ? `max-h-[${height}px]` : 'max-h-0'
        )}
      >
        {open && (
          <>
            {!isHiddenBorder && <div className="mx-6 h-px bg-blue-100" />}

            <div className={cn('px-7.5 py-6', sectionClassName)}>{children}</div>
          </>
        )}
      </div>
    </div>
  );
};
